import { Component, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Loading } from 'src/shared/components/loading/loading';
import { Timeout } from 'src/shared/components/timeout/timeout';
import { TimeoutErrorHandler } from 'src/shared/providers/http/timeout-error-handler';
import { ActivatedRoute, Router } from '@angular/router';
import { Credentials, CredentialTypeModel } from 'src/providers/credentials/credentials';
import { Aws } from 'src/shared/providers/aws';
import { SuccessPageParamsModel } from 'src/pages/success/success';
import { randomUUID } from 'src/tidy-ui-components/utils/secure-utils';

@Component({
  selector: 'add-credential',
  templateUrl: 'add-credential.html',
  encapsulation: ViewEncapsulation.None
})
export class AddCredentialPage extends Timeout {
  form: UntypedFormGroup;
  submitted: boolean;
  errorMessage: string;
  categories: { value: string; viewValue: string }[];
  types: { value: string; viewValue: string }[];
  selectedCategory: string;
  selectedType: string;
  showTextInput: boolean;
  showFileUpload: boolean;
  textInputLabel: string;
  textInputPlaceholder: string;
  uploadedImages: any[] = [];
  uploadedFileUrl: string;
  isUploading: boolean;
  prefillCategory: string;
  prefillCredentialType: string;
  prefillCredentialLabel: string;
  returnTo: string;
  returnCleaningId: string;
  credentialTypes: CredentialTypeModel[] = [];

  constructor(
    private credentialsProvider: Credentials,
    private customNavCtrl: CustomNavController,
    private fb: UntypedFormBuilder,
    private aws: Aws,
    timeoutErrorHandler: TimeoutErrorHandler,
    router: Router,
    route: ActivatedRoute
  ) {
    super(timeoutErrorHandler, router, route);
    this.form = this.fb.group({
      category: ['', Validators.required],
      credential_type: ['', Validators.required],
      text_value: ['']
    });
  }

  @Loading()
  async ionViewDidEnter() {
    try {
      this.credentialTypes = await this.credentialsProvider.getCredentialTypes();

      const categoryMap: Record<string, string> = {};
      for (const ct of this.credentialTypes) {
        categoryMap[ct.category] = ct.category_label;
      }
      this.categories = Object.keys(categoryMap).map(key => ({
        value: key,
        viewValue: categoryMap[key]
      }));
      this.resetForm();

      // Check for pre-fill params from claim-job page
      const navState = history.state;
      if (navState?.category && navState?.type) {
        this.prefillCategory = navState.category;
        this.prefillCredentialType = navState.type;
        const matchingType = this.credentialTypes.find(ct => ct.key === navState.type);
        this.prefillCredentialLabel = matchingType?.key_label || navState.type;
        this.returnTo = navState.returnTo || null;
        this.returnCleaningId = navState.cleaningId || null;

        // Auto-select category and type
        this.form.patchValue({ category: this.prefillCategory });
        this.onCategoryChange(this.prefillCategory);
        this.form.patchValue({ credential_type: this.prefillCredentialType });
        this.onTypeChange(this.prefillCredentialType);
      }

      this.loaded = true;
    } catch (err) {
      this.timeoutHandler(err);
    }
  }

  resetForm() {
    this.selectedCategory = null;
    this.selectedType = null;
    this.showTextInput = false;
    this.showFileUpload = false;
    this.uploadedImages = [];
    this.uploadedFileUrl = null;
    this.isUploading = false;
    this.types = [];
    this.submitted = false;
    this.errorMessage = '';
    this.form.reset({ category: '', credential_type: '', text_value: '' });
  }

  onCategoryChange(category: string) {
    this.selectedCategory = category;
    this.selectedType = null;
    this.showTextInput = false;
    this.showFileUpload = false;
    this.uploadedImages = [];
    this.uploadedFileUrl = null;
    this.errorMessage = '';
    this.form.patchValue({ credential_type: '', text_value: '' });
    this.form.get('text_value').clearValidators();
    this.form.get('text_value').updateValueAndValidity();

    this.types = this.credentialTypes
      .filter(ct => ct.category === category)
      .map(ct => ({
        value: ct.key,
        viewValue: ct.key_label
      }));
  }

  onTypeChange(type: string) {
    this.selectedType = type;
    this.errorMessage = '';
    this.uploadedImages = [];
    this.uploadedFileUrl = null;
    const config = this.credentialTypes.find(ct => ct.key === type);
    if (!config) return;

    const formOptions = config.form_options || {};

    const inputType = formOptions.input_type || 'file';
    if (inputType === 'url' || inputType === 'textarea') {
      this.showTextInput = true;
      this.showFileUpload = false;
      this.textInputLabel = inputType === 'url' ? 'URL' : 'Description';
      this.textInputPlaceholder = formOptions.placeholder || (inputType === 'url' ? 'https://...' : '');
      this.form.get('text_value').setValidators([Validators.required]);
    } else {
      this.showTextInput = false;
      this.showFileUpload = inputType === 'file';
      this.form.get('text_value').clearValidators();
      this.form.patchValue({ text_value: '' });
    }
    this.form.get('text_value').updateValueAndValidity();
  }

  async onFilesAdded(images: any[]) {
    this.errorMessage = '';
    this.isUploading = true;
    try {
      const image = images[images.length - 1];
      const s3Key = `homekeeper-credentials/${randomUUID()}`;
      const response = await this.aws.uploadImageToS3(image.url, s3Key, image.file, image.exif);
      this.uploadedFileUrl = response.Location;
      this.uploadedImages = images;
    } catch (err) {
      this.errorMessage = 'Failed to upload file. Please try again.';
      this.uploadedImages = [];
      this.uploadedFileUrl = null;
    } finally {
      this.isUploading = false;
    }
  }

  onFileRemoved(image: any) {
    this.uploadedImages = [];
    this.uploadedFileUrl = null;
  }

  onFileError(error: any) {
    this.errorMessage = error?.message || 'Invalid file. Please try a different file.';
  }

  submit = async () => {
    this.errorMessage = '';
    this.submitted = true;
    try {
      if (!this.form.value.credential_type) {
        this.errorMessage = 'Please select a credential type.';
        return;
      }
      if (this.showTextInput && !this.form.value.text_value) {
        this.errorMessage = this.textInputLabel === 'URL' ? 'Please enter a valid URL.' : 'Please enter a description.';
        return;
      }
      if (this.showFileUpload && !this.uploadedFileUrl) {
        this.errorMessage = 'Please upload a file.';
        return;
      }

      const data = {
        credential_type_key: this.form.value.credential_type,
        text_value: this.form.value.text_value || undefined,
        file_url: this.uploadedFileUrl || undefined
      };

      await this.credentialsProvider.addCredential(data);

      if (this.returnTo === 'claim-job') {
        this.customNavCtrl.navigateForward(this.returnCleaningId ? `claim-job/${this.returnCleaningId}` : 'claim-job', {
          returnedFromCredential: true
        });
        return;
      }

      const params = this.getSuccessPageParams();
      this.customNavCtrl.navigateRoot('success', params);
    } catch (err) {
      this.errorMessage = err.message || err.error?.message;
    }
  }

  getSuccessPageParams(): SuccessPageParamsModel {
    return {
      header: 'Credential Submitted',
      body: 'Your credential has been submitted and is pending review by our team. You will be notified once it has been reviewed.',
      buttonText: 'Ok',
      buttonRoute: 'business-profile'
    };
  }
}
